<template>
  <!--
    Teleported to <body>, never rendered in place — the same two verified reasons
    as AuthProgressOverlay.vue: `ion-router-outlet` / `.ion-page` carry
    `contain: layout` (a fixed wall inside a page is fixed to the PAGE) and a
    `z-index: 0` stacking context (it could never out-rank an Ionic overlay), and
    `inert` goes on <ion-app>, where Ionic appends its overlays — a wall rendered
    inside it would inert itself.

    `v-if` (a real unmount), never opacity/visibility: Ionic's capture-phase
    document `focus` listener yanks focus back into a presented modal, so a
    focusable Reload button in a merely-hidden wall would fight it.

    `data-no-theme-tap`: the app-wide double-tap theme toggle (useDoubleTapTheme)
    listens on document `pointerup`; tapping twice on a wall must not flip the
    theme underneath it.
  -->
  <Teleport to="body">
    <transition name="update-progress">
      <div v-if="open" class="update-progress" :aria-busy="phase !== 'stalled'" data-no-theme-tap>
        <div class="update-progress__panel">
          <!-- Decorative: the status text below IS the message. -->
          <div class="update-progress__emblem" aria-hidden="true">
            <span class="update-progress__halo" />
            <ion-spinner
              name="crescent"
              class="update-progress__ring"
              :duration="1200"
              :paused="phase === 'stalled'"
            />
            <span class="update-progress__disc">
              <img src="/assets/logo/syntry-mark.svg" alt="" class="update-progress__mark" />
            </span>
          </div>

          <p class="syntry-eyebrow update-progress__eyebrow">App update</p>

          <!--
            The live region, and ONLY the text that changes. `aria-busy` sits on the
            scrim, not here: `aria-busy="true"` on a live region makes assistive tech
            WITHHOLD its announcements (see AuthProgressOverlay.vue).
          -->
          <div class="update-progress__status" role="status" aria-live="polite" aria-atomic="true">
            <h2 ref="titleEl" class="update-progress__title" tabindex="-1">{{ copy.title }}</h2>
            <p class="update-progress__subtitle">{{ copy.subtitle }}</p>
            <p v-if="note" class="update-progress__note">{{ note }}</p>
          </div>

          <div v-if="phase !== 'stalled'" class="update-progress__bar" aria-hidden="true">
            <span class="update-progress__bar-fill" />
          </div>

          <!-- Outside the live region: it never changes mid-wait worth announcing. -->
          <p class="update-progress__versions">
            <template v-if="toVersion">
              <span>v{{ fromVersion }}</span>
              <span class="update-progress__arrow" aria-hidden="true">→</span>
              <span class="update-progress__sr">to</span>
              <span class="update-progress__to">v{{ toVersion }}</span>
            </template>
            <template v-else>You're on v{{ fromVersion }}</template>
          </p>

          <!--
            Outside the live region for the reason AuthProgressOverlay gives for its
            Cancel: a control inside `role="status"` is flattened into prose and
            re-announced with every change. The stalled line inside the region
            announces it. No focus is stolen when it appears (WCAG 2.2 SC 3.2.2);
            everything else is inert, so one Tab or swipe reaches it.
          -->
          <transition name="update-progress-reload">
            <button
              v-if="phase === 'stalled'"
              type="button"
              class="update-progress__reload"
              @click="emit('reload')"
            >
              Reload
            </button>
          </transition>

          <p class="update-progress__sr">
            An update is being installed. The rest of the screen is unavailable until Syntry restarts.
          </p>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * The update wall — a full-screen, genuinely blocking "Updating Syntry…" state
 * held over the app from the moment Update is tapped until the page reloads
 * onto the new build. Presentational: `UpdateProgressHost.vue` feeds it from
 * `src/pwa/update-session.ts`.
 *
 * Frosted, not opaque (the look chosen for this wall): the app stays faintly
 * visible behind the blur, so it reads as "the app is being updated" rather
 * than as a different screen. The panel itself is an opaque surface card.
 *
 * ── Status region, not a dialog ─────────────────────────────────────────────
 * The same reading of a11y-mobile's "no custom <div> modals" rule as the sign-in
 * wall (AuthProgressOverlay.vue): it contains nothing focusable until the rare
 * `stalled` state, demands no decision, and blocks with `inert` — the platform
 * primitive — instead of a hand-rolled focus trap. One Reload button does not
 * make a status region a dialog.
 *
 * ── Focus contract ──────────────────────────────────────────────────────────
 * On open: remember and blur the focused element (drops the soft keyboard),
 * inert <ion-app>, focus the title — inerting a subtree that holds focus would
 * otherwise orphan it on <body>. While open, a `focusout` keeper puts focus back
 * on the title if it ever falls to <body> with the document still focused —
 * an Ionic overlay presenting underneath calls `blur()`. That is not a trap:
 * focus leaving the document (browser chrome) is left alone. On close: lift
 * `inert` first (focus() on an inert subtree is a silent no-op), then restore
 * focus if the remembered element is still connected.
 *
 * ── Reduced motion ──────────────────────────────────────────────────────────
 * The halo's breathe and the bar's sweep are declared ONLY under
 * `prefers-reduced-motion: no-preference` — global.scss's reduce guard zeroes
 * `animation-duration` without clearing `animation-name`, so an infinite
 * animation there would loop a thousand times a second rather than stop. Under
 * reduce the bar is hidden and the halo is still; the crescent keeps turning,
 * as on the sign-in wall — it is shadow-encapsulated (no document rule reaches
 * it) and an in-place rotation is the one signal that the app has not hung.
 */
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { IonSpinner } from '@ionic/vue';

export type UpdateOverlayPhase = 'updating' | 'restarting' | 'stalled';

const props = withDefaults(
  defineProps<{
    /** Show the wall. */
    open: boolean;
    /** Which part of the update is running. */
    phase: UpdateOverlayPhase;
    /** The wait has become longer than usual — add the reassurance line. */
    slow?: boolean;
    /** The version running now. */
    fromVersion: string;
    /** The version being moved to, once known. */
    toVersion?: string | null;
  }>(),
  { slow: false, toVersion: null },
);

const emit = defineEmits<{ (e: 'reload'): void }>();

/** Hard-coded literals, per the prevailing convention for app chrome (see AuthProgressOverlay). */
const WAITING = {
  title: 'Updating Syntry…',
  subtitle: 'Please wait while the latest version is installed. Syntry will restart on its own.',
};
const COPY: Readonly<Record<UpdateOverlayPhase, { title: string; subtitle: string }>> = {
  updating: WAITING,
  restarting: { title: 'Restarting Syntry…', subtitle: 'The update is installed.' },
  stalled: WAITING,
};
const SLOW_LINE = 'This is taking longer than usual.';
const STALLED_LINE = "Syntry hasn't restarted yet. Check your connection, then tap Reload.";

const copy = computed(() => COPY[props.phase]);
const note = computed(() => {
  if (props.phase === 'stalled') return STALLED_LINE;
  return props.slow && props.phase === 'updating' ? SLOW_LINE : '';
});

// ── Shell inertness + focus ──────────────────────────────────────────────────

const titleEl = ref<HTMLHeadingElement | null>(null);

/** The element focus was taken from, restored on close if it is still on screen. */
let trigger: HTMLElement | null = null;
/** The node currently wearing `inert`, so release is exact and idempotent. */
let inerted: Element | null = null;

function lockShell(): void {
  if (inerted) return;
  const root = document.querySelector('ion-app');
  if (!root) return;
  root.setAttribute('inert', '');
  inerted = root;
}

function unlockShell(): void {
  inerted?.removeAttribute('inert');
  inerted = null;
}

/** Put focus back on the title when it drops to <body> — see the focus contract. */
function onDocumentFocusOut(): void {
  // Read the destination once the blur has settled.
  setTimeout(() => {
    if (!props.open) return;
    const active = document.activeElement;
    if ((active === null || active === document.body) && document.hasFocus()) {
      titleEl.value?.focus();
    }
  }, 0);
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      trigger = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      trigger?.blur();
      lockShell();
      document.addEventListener('focusout', onDocumentFocusOut, true);
      // One tick: the <transition> inserts in the same patch.
      await nextTick();
      titleEl.value?.focus();
      return;
    }
    document.removeEventListener('focusout', onDocumentFocusOut, true);
    // Order matters — focus() on an inert subtree is a silent no-op.
    unlockShell();
    if (trigger?.isConnected) trigger.focus();
    trigger = null;
  },
  { immediate: true },
);

// A host torn down mid-flight must never leave the app permanently inert.
onBeforeUnmount(() => {
  document.removeEventListener('focusout', onDocumentFocusOut, true);
  unlockShell();
});
</script>

<style scoped lang="scss">
.update-progress {
  position: fixed;
  inset: 0;
  /* Above EVERY Ionic overlay band — modal/alert 20000+, loading 40000+, toast
     60000+ (all drifting upward with Ionic's session-long overlay counter) — so a
     realtime toast can never paint a tappable card on a wall whose contract is
     that nothing behind it is reachable. Shares 70000 with the sign-in wall; the
     two never coexist (an update cannot be started while sign-in is running).
     House ladder: AuthProgressOverlay.vue. */
  z-index: 70000;

  display: flex;
  overflow-y: auto;
  /* Stop a flick at the wall from scrolling the inert page behind it. */
  overscroll-behavior: contain;
  padding: calc(var(--sp-8) + var(--syntry-safe-top, env(safe-area-inset-top, 0px))) var(--sp-6)
    calc(var(--sp-8) + var(--syntry-safe-bottom, env(safe-area-inset-bottom, 0px)));

  /* Frosted: the app stays faintly visible (see the component docblock).
     --syntry-surface-rgb flips with the theme, so dark mode frosts dark. */
  background: rgba(var(--syntry-surface-rgb), 0.72);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  color: var(--syntry-text);
  font-family: var(--syntry-font);

  &__panel {
    /* `margin: auto` centres AND stays scrollable when taller than the viewport
       (200% OS text size) — a centred flex child would be clipped at the top. */
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-3);
    width: 100%;
    max-width: 400px;
    text-align: center;
    padding: var(--sp-7) var(--sp-6);
    background: var(--syntry-surface);
    border: 1px solid var(--syntry-border);
    border-radius: var(--syntry-radius-card);
    box-shadow: var(--syntry-shadow-elevated);
  }

  /* --syntry-mark*, never --syntry-primary*: this is PRODUCT chrome, and
     syntry-mark.svg has the product teal baked into its pixels — a tenant's
     repainted primary would strand it in a red or amber halo (brand-color.ts). */
  &__emblem {
    position: relative;
    width: 104px;
    height: 104px;
    display: grid;
    place-items: center;
    margin-bottom: var(--sp-2);
  }

  &__halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: var(--syntry-mark-soft);
  }

  /* The crescent's circle sits at r=26 of a 64-unit box, so at 104px it rings the
     68px disc with a few px of air. Ionic's documented CSS var, not `color`. */
  &__ring {
    position: absolute;
    inset: 0;
    width: 104px;
    height: 104px;
    --color: var(--syntry-mark);
  }

  &__disc {
    position: relative;
    width: 68px;
    height: 68px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--syntry-surface);
    box-shadow: 0 4px 14px rgba(var(--syntry-mark-rgb), 0.2);
  }

  &__mark {
    width: 38px;
    height: 38px;
  }

  &__eyebrow {
    margin: 0;
    /* rem, not the px --syntry-fs-* tokens, so OS dynamic type still applies. */
    font-size: 0.75rem;
  }

  &__status {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--sp-2);
  }

  &__title {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.01em;
    color: var(--syntry-text-strong);

    /* tabindex="-1": programmatically focusable, never tab-reachable — a ring
       would only draw a box the instant the wall opens. */
    &:focus {
      outline: none;
    }
  }

  &__subtitle {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    color: var(--syntry-text-muted);
    max-width: 30ch;
  }

  &__note {
    margin: var(--sp-1) 0 0;
    font-size: 0.875rem;
    line-height: 1.45;
    max-width: 32ch;
    color: var(--syntry-text-muted);
  }

  &__bar {
    position: relative;
    width: 160px;
    height: 4px;
    margin-top: var(--sp-2);
    border-radius: 999px;
    overflow: hidden;
    background: rgba(var(--syntry-mark-rgb), 0.16);
  }

  &__bar-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 40%;
    border-radius: inherit;
    background: linear-gradient(90deg, var(--syntry-mark), var(--syntry-mark-dark));
    transform: translateX(-100%);
  }

  &__versions {
    margin: var(--sp-1) 0 0;
    display: inline-flex;
    align-items: center;
    gap: var(--sp-2);
    font-size: 0.8125rem;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    color: var(--syntry-text-muted);
  }

  /* --syntry-mark-dark, not --syntry-mark: 4.9:1 on the light surface for this
     13px text where the lighter teal measures 3.3:1. */
  &__to {
    color: var(--syntry-mark-dark);
  }

  /* Primary CTA (the design system's brand-first rule), painted from the mark
     tokens like the rest of this product-chrome screen. */
  &__reload {
    margin-top: var(--sp-3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 52px;
    min-width: 200px;
    padding: 0 var(--sp-6);
    border: none;
    border-radius: var(--syntry-radius-button);
    background: var(--syntry-mark);
    color: #ffffff;
    box-shadow: var(--syntry-shadow-button);
    font-family: var(--syntry-font);
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform 160ms ease;

    &:active {
      transform: translateY(1px);
    }

    &:focus-visible {
      outline: 2px solid var(--syntry-mark);
      outline-offset: 3px;
    }
  }

  /* Visually hidden, still announced. */
  &__sr {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

/* The waiting animations — declared ONLY here (see the docblock). */
@media (prefers-reduced-motion: no-preference) {
  .update-progress__halo {
    animation: update-progress-breathe 2.4s ease-in-out infinite;
  }

  .update-progress__bar-fill {
    animation: update-progress-sweep 1.5s cubic-bezier(0.45, 0, 0.55, 1) infinite;
  }

  @keyframes update-progress-breathe {
    0%,
    100% {
      transform: scale(0.9);
      opacity: 0.95;
    }

    50% {
      transform: scale(1.08);
      opacity: 0.45;
    }
  }

  @keyframes update-progress-sweep {
    from {
      transform: translateX(-100%);
    }

    to {
      transform: translateX(250%);
    }
  }
}

/* House enter/leave pair (useToast.ts's curves): the panel carries the travel,
   the scrim only fades. */
.update-progress-enter-active {
  transition: opacity 260ms cubic-bezier(0.32, 0.72, 0, 1);

  .update-progress__panel {
    transition: transform 260ms cubic-bezier(0.32, 0.72, 0, 1),
      opacity 260ms cubic-bezier(0.32, 0.72, 0, 1);
  }
}

.update-progress-leave-active {
  transition: opacity 220ms cubic-bezier(0.4, 0, 1, 1);

  .update-progress__panel {
    transition: transform 220ms cubic-bezier(0.4, 0, 1, 1),
      opacity 220ms cubic-bezier(0.4, 0, 1, 1);
  }
}

.update-progress-enter-from,
.update-progress-leave-to {
  opacity: 0;

  .update-progress__panel {
    transform: translateY(8px) scale(0.98);
    opacity: 0;
  }
}

.update-progress-reload-enter-active {
  transition: opacity 200ms cubic-bezier(0.32, 0.72, 0, 1),
    transform 200ms cubic-bezier(0.32, 0.72, 0, 1);
}

.update-progress-reload-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

@media (prefers-reduced-motion: reduce) {
  /* A static bar says nothing about progress — the spinner carries it. */
  .update-progress__bar {
    display: none;
  }

  .update-progress-enter-active,
  .update-progress-leave-active,
  .update-progress-reload-enter-active {
    transition: none;

    .update-progress__panel {
      transition: none;
    }
  }

  .update-progress__reload {
    transition: none;
  }
}
</style>
